import { useEffect, useState } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import Helpers from "@/Helpers";
import { toast } from "react-toastify";
import Enums from "@/enums";
import { displayClientIp } from "@/utils/displayClientIp";

type AuditLogModalProps = {
  isOpen: boolean;
  logId: number | null;
  onClose: () => void;
  updateSpinner?: ((show: boolean) => void) | null;
};

type AuditLogDetails = {
  id?: number;
  when?: string;
  severity?: number | string;
  actionId?: number | string;
  entityTypeId?: number | string | null;
  entityId?: number | null;
  message?: string | null;
  context?: string | null;
  userId?: number | null;
  userName?: string | null;
  companyId?: number | null;
  companyName?: string | null;
  ipAddress?: string | null;
  language?: string | null;
  referrer?: string | null;
  userAgent?: string | null;
  stackTrace?: string | null;
};

function getSeverityName(severity: any) {
  switch (parseInt(severity)) {
    case Enums.LOG_SEVERITY.INFO:
      return "Info";
    case Enums.LOG_SEVERITY.DEBUG:
      return "Debug";
    case Enums.LOG_SEVERITY.WARNING:
      return "Warning";
    case Enums.LOG_SEVERITY.ERROR:
      return "Error";
    case Enums.LOG_SEVERITY.EXCEPTION:
      return "Exception";
    case Enums.LOG_SEVERITY.CRITICAL:
      return "Critical";
    default:
      return String(severity ?? "N/A");
  }
}

export default function AuditLogModal({ isOpen, logId, onClose, updateSpinner = null }: AuditLogModalProps) {
  const [details, setDetails] = useState<AuditLogDetails | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadDetailsAsync() {
      if (!isOpen || !logId) {
        if (isMounted) setDetails(null);
        return;
      }

      setLoading(true);
      updateSpinner?.(true);
      const resp = await RightTimeApi.getAsync(`/SystemLog/${logId}`);
      updateSpinner?.(false);
      setLoading(false);

      if (!isMounted) return;

      if (resp?.ok && resp?.jsonData) {
        setDetails(resp.jsonData);
        return;
      }

      setDetails(null);
      toast.error(`Failed to load audit log entry ${logId}`);
    }

    void loadDetailsAsync();
    return () => {
      isMounted = false;
    };
  }, [isOpen, logId, updateSpinner]);

  const footer = (
    <div className="footerCenter">
      <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
    </div>
  );

  const valueOrNA = (value: unknown) => {
    if (value === null || value === undefined || value === "") return "N/A";
    return String(value);
  };

  return (
    <Modal
      isOpen={!!isOpen}
      onClose={onClose}
      title={details?.id ? `Audit Log Entry ${details.id}` : "Audit Log Entry"}
      footer={footer}
      className="auditLogModal"
    >
      {loading ? (
        <div className="center">Loading...</div>
      ) : !details ? (
        <div className="center">No details available.</div>
      ) : (
        <div className="adminTable auditLogDetailsTable">
          <table>
            <tbody>
              <tr><th>ID</th><td>{valueOrNA(details.id)}</td></tr>
              <tr><th>Timestamp</th><td>{details.when ? Helpers.EST(details.when) : "N/A"}</td></tr>
              <tr><th>Severity</th><td>{getSeverityName(details.severity)}</td></tr>
              <tr><th>Action Id</th><td>{valueOrNA(details.actionId)}</td></tr>
              <tr><th>Entity Type</th><td>{valueOrNA(details.entityTypeId)}</td></tr>
              <tr><th>Entity Id</th><td>{valueOrNA(details.entityId)}</td></tr>
              <tr><th>Message</th><td>{valueOrNA(details.message)}</td></tr>
              <tr><th>Context</th><td>{valueOrNA(details.context)}</td></tr>
              <tr><th>User</th><td>{valueOrNA(details.userName || details.userId)}</td></tr>
              <tr><th>Company</th><td>{valueOrNA(details.companyName || details.companyId)}</td></tr>
              <tr><th>IP Address</th><td>{valueOrNA(displayClientIp(details.ipAddress) || null)}</td></tr>
              <tr><th>Language</th><td>{valueOrNA(details.language)}</td></tr>
              <tr><th>Referrer</th><td>{valueOrNA(details.referrer)}</td></tr>
              <tr><th>User Agent</th><td>{valueOrNA(details.userAgent)}</td></tr>
              <tr>
                <th>Stack Trace</th>
                <td>
                  {details.stackTrace ? (
                    <pre className="stackTraceValue">{details.stackTrace}</pre>
                  ) : (
                    "N/A"
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
